import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useMutation } from '@tanstack/react-query';
import {
  Reminder,
  ReminderCreateInput,
  ReminderType,
  ReminderEventType,
  NotificationChannel,
  Vehicle,
} from '@vehicle-logbook/shared';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatDateTimeLocal } from '../../utils/dateFormat';
import { Modal, ModalBody, ModalFooter } from '../common/Modal';
import { FormField } from '../common/FormField';
import {
  btnPrimary,
  btnSecondary,
  fieldGridClass,
  inputClass,
  selectClass,
  textareaClass,
} from '../common/formStyles';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

interface ReminderFormProps {
  reminder?: Reminder | null;
  vehicles: Vehicle[];
  onClose: () => void;
  onSuccess: () => void;
}

const eventTypeOptions: { value: ReminderEventType; label: string }[] = [
  { value: 'oil_change', label: 'Заміна масла' },
  { value: 'inspection', label: 'Техогляд' },
  { value: 'insurance', label: 'Страхування' },
  { value: 'tire_change', label: 'Заміна шин' },
  { value: 'custom', label: 'Кастомне' },
];

export const ReminderForm = ({
  reminder,
  vehicles,
  onClose,
  onSuccess,
}: ReminderFormProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [formData, setFormData] = useState<ReminderCreateInput>({
    vehicleId: vehicles[0]?.id || '',
    title: '',
    description: '',
    type: 'date',
    eventType: 'custom',
    triggerValue: new Date(),
    notificationChannels: ['email'],
    interval: 1,
    intervalUnit: 'months',
  });

  useEffect(() => {
    if (reminder) {
      let triggerDate: Date;
      try {
        const triggerValue = reminder.triggerValue;
        if (
          triggerValue &&
          typeof triggerValue === 'object' &&
          'toDate' in triggerValue &&
          typeof triggerValue.toDate === 'function'
        ) {
          triggerDate = triggerValue.toDate();
        } else if (triggerValue instanceof Date) {
          triggerDate = triggerValue;
        } else if (
          typeof triggerValue === 'string' ||
          typeof triggerValue === 'number'
        ) {
          triggerDate = new Date(triggerValue);
        } else if (
          triggerValue &&
          typeof triggerValue === 'object' &&
          'seconds' in triggerValue &&
          typeof triggerValue.seconds === 'number'
        ) {
          triggerDate = new Date(triggerValue.seconds * 1000);
        } else if (
          triggerValue &&
          typeof triggerValue === 'object' &&
          '_seconds' in triggerValue &&
          typeof triggerValue._seconds === 'number'
        ) {
          triggerDate = new Date(triggerValue._seconds * 1000);
        } else {
          triggerDate = new Date();
        }
        if (isNaN(triggerDate.getTime())) {
          triggerDate = new Date();
        }
      } catch (error) {
        triggerDate = new Date();
      }

      setFormData({
        vehicleId: reminder.vehicleId,
        title: reminder.title,
        description: reminder.description || '',
        type: reminder.type,
        eventType: reminder.eventType,
        triggerValue: triggerDate,
        notificationChannels: reminder.notificationChannels || ['email'],
        interval: reminder.interval || 1,
        intervalUnit: reminder.intervalUnit || 'months',
      });
    }
  }, [reminder]);

  useEffect(() => {
    if (!reminder && vehicles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        vehicleId: prev.vehicleId || vehicles[0].id,
      }));
    }
  }, [vehicles, reminder]);

  const createMutation = useMutation({
    mutationFn: async (data: ReminderCreateInput) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const response = await axios.post(`${API_URL}/reminders`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('reminder.addSuccess'));
      onSuccess();
    },
    onError: (error: unknown) => {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response
              ?.data?.error
          : undefined;
      toast.error(errorMessage || t('errors.generic'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<ReminderCreateInput>) => {
      if (!user || !reminder) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const response = await axios.put(
        `${API_URL}/reminders/${reminder.id}`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('reminder.updateSuccess'));
      onSuccess();
    },
    onError: (error: unknown) => {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response
              ?.data?.error
          : undefined;
      toast.error(errorMessage || t('errors.generic'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.vehicleId) {
      toast.error(t('reminder.selectVehicle'));
      return;
    }

    if (
      formData.type === 'recurring' &&
      (!formData.interval || !formData.intervalUnit)
    ) {
      toast.error(t('reminder.intervalRequired'));
      return;
    }

    if (formData.notificationChannels.length === 0) {
      toast.error(t('reminder.channelsRequired'));
      return;
    }

    const submitData = {
      ...formData,
      triggerValue:
        formData.triggerValue instanceof Date
          ? formData.triggerValue
          : new Date(formData.triggerValue),
    };

    if (reminder) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const toggleChannel = (channel: NotificationChannel) => {
    setFormData((prev) => ({
      ...prev,
      notificationChannels: prev.notificationChannels.includes(channel)
        ? prev.notificationChannels.filter((c) => c !== channel)
        : [...prev.notificationChannels, channel],
    }));
  };

  return (
    <Modal
      title={reminder ? t('reminder.edit') : t('reminder.add')}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody>
          <div className="space-y-3">
            <FormField label={t('nav.vehicles')} required>
              <select
                value={formData.vehicleId}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    vehicleId: e.target.value,
                  }))
                }
                required
                className={selectClass}
              >
                <option value="">
                  {t('common.select')} {t('nav.vehicles')}
                </option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.name} ({vehicle.make} {vehicle.model})
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={t('reminder.eventType')} required>
              <select
                value={formData.eventType}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    eventType: e.target.value as ReminderEventType,
                  }))
                }
                required
                className={selectClass}
              >
                {eventTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label={t('reminder.title')} required>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                required
                maxLength={200}
                className={inputClass}
              />
            </FormField>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('reminder.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={2}
                className={textareaClass}
              />
            </div>

            <FormField label={t('reminder.type')} required>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    type: e.target.value as ReminderType,
                  }))
                }
                required
                className={selectClass}
              >
                <option value="date">{t('reminder.types.date')}</option>
                <option value="recurring">
                  {t('reminder.types.recurring')}
                </option>
              </select>
            </FormField>

            {formData.type === 'date' ? (
              <FormField label={t('reminder.triggerDate')} required>
                <input
                  type="datetime-local"
                  value={formatDateTimeLocal(formData.triggerValue)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      triggerValue: new Date(e.target.value),
                    }))
                  }
                  required
                  className={inputClass}
                />
              </FormField>
            ) : (
              <>
                <FormField label={t('reminder.startDate')} required>
                  <input
                    type="datetime-local"
                    value={formatDateTimeLocal(formData.triggerValue)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        triggerValue: new Date(e.target.value),
                      }))
                    }
                    required
                    className={inputClass}
                  />
                </FormField>
                <div className={fieldGridClass}>
                  <FormField label={t('reminder.interval')} required>
                    <input
                      type="number"
                      min="1"
                      value={formData.interval}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          interval: parseInt(e.target.value, 10),
                        }))
                      }
                      required
                      className={inputClass}
                    />
                  </FormField>
                  <FormField label={t('reminder.intervalUnit')} required>
                    <select
                      value={formData.intervalUnit}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          intervalUnit: e.target.value as
                            | 'days'
                            | 'weeks'
                            | 'months',
                        }))
                      }
                      required
                      className={selectClass}
                    >
                      <option value="days">
                        {t('reminder.intervalUnits.days')}
                      </option>
                      <option value="weeks">
                        {t('reminder.intervalUnits.weeks')}
                      </option>
                      <option value="months">
                        {t('reminder.intervalUnits.months')}
                      </option>
                    </select>
                  </FormField>
                </div>
              </>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t('reminder.channels')} *
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.notificationChannels.includes('email')}
                    onChange={() => toggleChannel('email')}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>Email</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.notificationChannels.includes('telegram')}
                    onChange={() => toggleChannel('telegram')}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span>Telegram</span>
                </label>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <button type="button" onClick={onClose} className={btnSecondary}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className={btnPrimary}
          >
            {createMutation.isPending || updateMutation.isPending
              ? t('common.loading')
              : reminder
                ? t('common.save')
                : t('common.add')}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
